/**
 * Contacts API — talks to the backend.
 * Translates rich API data into the flat Contact shape the frontend pages use.
 */
import { api } from './api';
import type { Contact } from './contactsStore';

// ─── API response shape ─────────────────────────────────────

interface ApiContact {
  id: string;
  type: string;
  prefix: string | null;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
  notes: string | null;
  tags: string[];
  kycStatus: string;
  firmName: string | null;
  sraNumber: string | null;
  paymentTermsDays: number;
  createdAt: string;
  _count?: {
    layClientCases: number;
    instructingCases: number;
  };
  // Detail only
  layClientCases?: Array<{ id: string; caseNumber: string; caseName: string; status: string }>;
  instructingCases?: Array<{ id: string; caseNumber: string; caseName: string; status: string }>;
}

// ─── Transform API → frontend Contact shape ─────────────────

function contactName(c: ApiContact): string {
  if (c.type === 'firm') return c.company ?? '';
  const parts = [c.prefix, c.firstName, c.lastName].filter(Boolean);
  return parts.join(' ') || c.company || '';
}

function contactOrg(c: ApiContact): string {
  if (c.type === 'solicitor') return c.firmName ?? '';
  if (c.type === 'firm') return '—';
  return c.company ?? '—';
}

function typeToFrontend(apiType: string): string {
  const map: Record<string, string> = {
    lay_client: 'lay',
    defendant: 'defendant',
    solicitor: 'solicitor',
    firm: 'company',
    expert: 'expert',
    witness: 'witness',
    court_official: 'other',
    other: 'other',
  };
  return map[apiType] ?? apiType;
}

function kycToFrontend(status: string): string {
  const map: Record<string, string> = {
    verified: 'Valid',
    pending: 'Pending',
    in_progress: 'In progress',
    expired: 'Expiring',
    rejected: 'Rejected',
    not_required: '—',
  };
  return map[status] ?? '—';
}

function apiToContact(c: ApiContact): Contact {
  const caseCount = (c._count?.layClientCases ?? 0) + (c._count?.instructingCases ?? 0);
  return {
    id: c.id,
    type: typeToFrontend(c.type),
    name: contactName(c),
    org: contactOrg(c),
    email: c.email ?? '',
    phone: c.phone ?? '',
    mobilePhone: c.mobilePhone ?? '',
    cases: caseCount,
    kyc: kycToFrontend(c.kycStatus),
    tags: c.tags,
    notes: c.notes ?? '',
    createdAt: new Date(c.createdAt).getTime(),
  };
}

// ─── API calls ──────────────────────────────────────────────

export async function fetchContacts(): Promise<Contact[]> {
  const res = await api.get('/contacts', {
    params: { limit: 100, sortBy: 'createdAt', sortOrder: 'desc' },
  });
  const payload = res.data?.data ?? res.data;
  const contacts: ApiContact[] = payload.contacts ?? [];
  return contacts.map(apiToContact);
}

// Live search — used by ContactPicker (limit 8 for snappy suggestions) and the
// Contacts list page (higher limit to find any contact, not just loaded ones).
export async function searchContacts(query: string, type?: string, limit = 8): Promise<Contact[]> {
  if (!query || query.trim().length < 2) return [];
  const res = await api.get('/contacts', {
    params: {
      search: query.trim(),
      limit,
      ...(type && { type }),
      sortBy: 'createdAt',
      sortOrder: 'desc',
    },
  });
  const payload = res.data?.data ?? res.data;
  const contacts: ApiContact[] = payload.contacts ?? [];
  return contacts.map(apiToContact);
}

export async function fetchContact(id: string): Promise<Contact> {
  const res = await api.get(`/contacts/${id}`);
  const contact = (res.data as any).data ?? res.data;
  return apiToContact(contact as ApiContact);
}

export interface CreateContactPayload {
  type: string;
  prefix?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  notes?: string;
  tags?: string[];
  firmName?: string;
  sraNumber?: string;
  kycStatus?: string;
}

export async function createContact(payload: CreateContactPayload): Promise<Contact> {
  const res = await api.post('/contacts', payload);
  const contact = (res.data as any).data ?? res.data;
  return apiToContact(contact as ApiContact);
}

export async function updateContact(id: string, payload: Partial<CreateContactPayload>): Promise<Contact> {
  const res = await api.patch(`/contacts/${id}`, payload);
  const contact = (res.data as any).data ?? res.data;
  return apiToContact(contact as ApiContact);
}

export async function deleteContact(id: string): Promise<void> {
  await api.delete(`/contacts/${id}`);
}
